<template>
  <article class="basket-combo-card">
    <!-- Верхняя часть: изображение и название -->
    <div class="basket-combo-card__top">
      <!-- Изображение комбо: если слоты расставлены — собираем из того, что заказали -->
      <ComboScene
        v-if="sceneLayers.length > 0"
        :layers="sceneLayers"
        class="basket-combo-card__scene"
      />
      <BasketProductImage v-else :images="item.img" :name="item.name" />

      <!-- Название -->
      <div class="basket-combo-card__name">
        {{ item.name }}
        <span class="basket-combo-card__badge">КОМБО</span>
      </div>

      <UiBaseCounter
        size="sm"
        :count-v="item.quantity"
        :step="quantityStep"
        :min-v="minQuantity"
        class="basket-combo-card__counter"
        @count="handleQuantityChange"
      />
    </div>

    <!-- Состав комбо -->
    <div v-if="selectedProducts.length > 0" class="basket-combo-card__composition">
      <div class="basket-combo-card__composition-title">Состав:</div>
      <div class="basket-combo-card__composition-items">
        <div
          v-for="(selection, index) in selectedProducts"
          :key="index"
          class="basket-combo-card__composition-item"
        >
          <span v-if="selection.groupName" class="basket-combo-card__composition-group">{{ selection.groupName }}:</span>
          <span class="basket-combo-card__composition-products">
            <template v-for="(product, i) in selection.products" :key="product.id">
              <span v-if="i > 0">, </span>{{ product.name
              }}<span v-if="product.size" class="basket-combo-card__composition-size">{{
                product.size
              }}</span>
            </template>
          </span>
          <!-- Изменённый состав слота: клиент за это платит, значит видит -->
          <div v-if="selection.modifiersLabel" class="basket-combo-card__composition-modifiers">
            {{ selection.modifiersLabel }}
          </div>
        </div>
      </div>
    </div>

    <!-- ⚠️ Комбо не продаётся в выбранном филиале (например, после его смены) -->
    <div v-if="isUnavailable" class="basket-combo-card__warning">
      <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <div>
        <p class="font-semibold">Комбо недоступно</p>
        <p class="text-xs">Этого набора нет в выбранном филиале — удалите его из корзины, чтобы оформить заказ</p>
      </div>
    </div>

    <!-- Набор продаётся, но блюдо слота в этом филиале не готовят -->
    <div v-else-if="needsSlotChange" class="basket-combo-card__notice">
      <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
      </svg>
      <div>
        <p class="font-semibold">Замените блюдо в наборе</p>
        <p class="text-xs">В этом филиале одно из выбранных блюд не готовят — соберите набор заново</p>
      </div>
    </div>

    <!-- Скидка -->
    <div v-if="showDiscount" class="basket-combo-card__discount">
      <span class="basket-combo-card__discount-label">
        Скидка {{ discountPercent }}%:
      </span>
      <span class="basket-combo-card__discount-value">
        -{{ formatPrice(discountAmount) }} ₽
      </span>
    </div>

    <!-- Итоговая цена -->
    <div class="basket-combo-card__price">
      <span>Итого</span>
      <div class="basket-combo-card__price-value">
        <span v-if="basePrice && showDiscount" class="basket-combo-card__price-old">
          {{ formatPrice(basePrice * item.quantity) }} ₽
        </span>
        {{ formatPrice(totalPrice) }} ₽
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import consola from 'consola';
import apiCartInstance, { useCartStore, getAddonName, getExcludeName } from '@entities/order';
import { useInstitutionStore } from '@entities/institution';
import { productSizeLabel } from '@entities/product';
import { ComboScene, comboSceneLayers } from '@entities/combo';

interface ComboCartItem {
  id: string;
  itemType: 'combo';
  comboId: string;
  name: string;
  quantity: number;
  price: number;
  img: string[];
  branch_id?: number;
  comboSelections?: Array<{
    group_id: string | number;
    product_ids: (string | number)[];
    modifiers?: Array<{ product_id: number; addons?: number[]; excludes?: number[] }>;
  }>;
  base_price?: number;
  discount_percent?: number;
}

interface Props {
  item: ComboCartItem;
}

const props = defineProps<Props>();

const cartStore = useCartStore();
const institutionStore = useInstitutionStore();

/**
 * Позиция в каталоге филиала вместе с её категорией.
 *
 * Категория нужна как запасное имя слота: карточки комбо в меню может не быть (комбо сняли
 * с филиала, а заказ в корзине остался), и тогда названия групп взять неоткуда — гость
 * видел «Группа 73» вместо «Шаурма».
 */
const findCatalogEntry = (
  productId: string | number,
): { product: any; category: string | null } | null => {
  const id = Number(productId);

  for (const category of institutionStore.products) {
    if (!category.products) continue;

    for (const product of category.products) {
      if (product.type === 'single' && product.id === id) {
        return { product, category: category.category ?? null };
      }

      if (product.type === 'group' && product.variants) {
        const variant = product.variants.find(v => v.id === id);
        if (variant) return { product: variant, category: category.category ?? null };
      }
    }
  }

  return null;
};

const findProductById = (productId: string | number): any =>
  findCatalogEntry(productId)?.product ?? null;

const comboData = computed(() => {
  const comboId = Number(props.item.comboId);

  for (const category of institutionStore.products) {
    if (!category.products) continue;

    const combo = category.products.find(
      p => p.type === 'combo' && p.id === comboId,
    );

    if (combo && combo.type === 'combo') return combo;
  }

  return null;
});

/**
 * «+ Моцарелла · Без лука» — кастомизация слота, выбранная в конструкторе комбо.
 *
 * Названия берём из каталога добавок заведения (getAddonName/getExcludeName):
 * добавки и отбавки — не позиции меню, поэтому поиском по товарам они не находятся
 * и в корзине выводились как «Товар 778».
 */
const modifiersLabelOf = (selection: ComboCartItem['comboSelections'][number]): string => {
  const added = (selection.modifiers ?? []).flatMap(m => (m.addons ?? []).map(getAddonName));
  // Отбавки в меню часто уже названы «Без огурца» — минус дал бы двойное отрицание
  const removed = (selection.modifiers ?? [])
    .flatMap(m => (m.excludes ?? []).map(getExcludeName))
    .map(name => (/^без\s/i.test(name) ? name : `− ${name}`));

  return [added.length > 0 ? `+ ${added.join(', ')}` : '', ...removed]
    .filter(Boolean)
    .join(' · ');
};

const selectedProducts = computed(() => {
  if (!props.item.comboSelections) return [];

  return props.item.comboSelections.map((selection) => {
    const group = comboData.value?.groups.find(g => g.id === Number(selection.group_id));

    /**
     * Имя слота: как назвал группу админ, иначе категория выбранного товара. Технический
     * «Группа 73» гостю не говорит ничего — лучше не показывать подпись совсем.
     */
    const categoryName = selection.product_ids
      .map(id => findCatalogEntry(id)?.category)
      .find(Boolean);
    const groupName = group?.name?.trim() || categoryName?.trim() || '';

    /**
     * «Ветчина и грибы 50см»: без размера состав комбо не читается — у пиццы, шаурмы
     * и напитков позиции различаются только им, и гость не понимает, что именно собрал.
     * Размер берём из каталога, а если позиции там нет — из состава группы комбо:
     * товар мог остаться только в комбо, и тогда каталог его не находит.
     */
    const products = selection.product_ids.map((id) => {
      const fromCatalog = findProductById(id);
      const fromGroup = group?.products.find(p => Number(p.id) === Number(id));

      return {
        id,
        name: fromCatalog?.name || fromGroup?.name || `Товар ${id}`,
        // У весового блюда вместо размера показываем порцию комбо («250 г»): в корзине
        // это единственное место, где видно, сколько именно мяса гость купил.
        size: fromGroup?.portion_label || productSizeLabel(fromCatalog ?? fromGroup),
      };
    });

    return { groupName, products, modifiersLabel: modifiersLabelOf(selection) };
  });
});

/**
 * Составная картинка комбо из ЗАКАЗАННЫХ позиций: гость должен узнать в корзине свой набор,
 * а не увидеть одну общую картинку комбо. Позицию слота берём из comboSelections, картинку
 * позиции — из состава группы; чего нет — просто не рисуем.
 */
const sceneLayers = computed(() =>
  comboSceneLayers(comboData.value?.groups, (group) => {
    const selection = props.item.comboSelections?.find(
      s => Number(s.group_id) === Number(group.id),
    );
    return selection?.product_ids?.[0];
  }),
);

const basePrice = computed(() => props.item.base_price);
/** Комбо помечено недоступным: его нет в меню выбранного филиала */
/** Набор доступен, но блюдо слота в этом филиале не готовят — нужна замена */
const needsSlotChange = computed(() => (props.item as any)?.combo_slot_unavailable === true);

const isUnavailable = computed(() => (props.item as any)?.stop_list === true);

const discountPercent = computed(() => props.item.discount_percent || 0);
const showDiscount = computed(() => discountPercent.value > 0 && basePrice.value);

const discountAmount = computed(() => {
  if (!showDiscount.value || !basePrice.value) return 0;
  return (basePrice.value * props.item.quantity * discountPercent.value) / 100;
});

const totalPrice = computed(() => props.item.price * props.item.quantity);

const branchId = computed(() => props.item.branch_id || institutionStore.selectedBranch?.id);

const quantityStep = computed(() => 1);
const minQuantity = computed(() => 0);

const formatPrice = (price: number): string => Math.round(price).toString();

const handleQuantityChange = async (newQuantity: number) => {
  if (!branchId.value) return;

  try {
    const currentQuantity = props.item.quantity;
    const delta = newQuantity - currentQuantity;

    if (newQuantity === 0) {
      await apiCartInstance.removeFromCart({
        item_type: 'combo',
        combo_id: Number(props.item.comboId),
        branch_id: String(branchId.value),
        combo_selections: props.item.comboSelections,
      });
      await cartStore.initializeCart();
    } else if (delta > 0) {
      await apiCartInstance.increaseQuantity({
        item_type: 'combo',
        combo_id: Number(props.item.comboId),
        branch_id: String(branchId.value),
        combo_selections: props.item.comboSelections,
        amount: delta,
      });
      await cartStore.initializeCart();
    } else if (delta < 0) {
      await apiCartInstance.decreaseQuantity({
        item_type: 'combo',
        combo_id: Number(props.item.comboId),
        branch_id: String(branchId.value),
        combo_selections: props.item.comboSelections,
        amount: Math.abs(delta),
      });
      await cartStore.initializeCart();
    }
  } catch (error) {
    consola.error('[BasketComboCard] Error updating combo quantity:', error);
  }
};
</script>

<style scoped lang="scss">
.basket-combo-card {
  // Нейтральный zinc вместо cool-gray (без синего отлива); оранжевый акцент комбо сохраняем
  @apply relative bg-gradient-to-br from-white to-gray-50 dark:from-zinc-900 dark:to-zinc-900/50 overflow-hidden;
  // Скругление из пресета «Скругления элементов»
  border-radius: var(--app-radius-card, 24px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  max-width: 100%;
  box-sizing: border-box;
  border: 2px solid #ff6b35;
  box-shadow:
    0 1px 3px rgba(255, 107, 53, 0.1),
    0 10px 15px -3px rgba(255, 107, 53, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow:
      0 4px 6px -1px rgba(255, 107, 53, 0.15),
      0 15px 20px -5px rgba(255, 107, 53, 0.15);
  }

  @media (prefers-color-scheme: dark) {
    border-color: #ff8c5a;
  }
}

.basket-combo-card__top {
  display: grid;
  grid-template-columns: 64px 1fr auto;
  gap: 12px;
  align-items: center;
  max-width: 100%;
}

.basket-combo-card__name {
  font-size: 16px;
  font-weight: 600;
  @apply text-gray-900 dark:text-gray-100;
  line-height: 1.3;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

// Место под миниатюру в шапке карточки — такое же, как у BasketProductImage (64px в гриде)
.basket-combo-card__scene {
  @apply w-16 h-16 overflow-hidden;
  border-radius: var(--app-radius-media, 16px);
}

.basket-combo-card__badge {
  display: inline-block;
  padding: 2px 8px;
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c5a 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  width: fit-content;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.basket-combo-card__composition {
  padding: 12px;
  @apply bg-orange-50 dark:bg-gray-800/50;
  border-radius: 8px;
  border-left: 3px solid #ff6b35;
  max-width: 100%;
  box-sizing: border-box;
}

.basket-combo-card__composition-title {
  font-size: 13px;
  font-weight: 600;
  @apply text-gray-700 dark:text-gray-300;
  margin-bottom: 8px;
}

.basket-combo-card__composition-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.basket-combo-card__composition-item {
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.basket-combo-card__composition-group {
  font-weight: 600;
  @apply text-gray-900 dark:text-gray-100;
  margin-right: 4px;
}

.basket-combo-card__composition-products {
  @apply text-gray-600 dark:text-gray-400;
}

// Размер — рядом с названием, но тише его: он уточняет позицию, а не называет её.
.basket-combo-card__composition-size {
  @apply ml-1 text-xs text-gray-500 dark:text-gray-500;
  white-space: nowrap;
}

.basket-combo-card__composition-modifiers {
  @apply text-xs text-red-500 mt-0.5;
}

.basket-combo-card__warning {
  @apply flex items-start gap-3 px-4 py-3;
  @apply bg-red-50 dark:bg-red-900/20;
  @apply border-t border-b border-red-200 dark:border-red-800;
  @apply text-red-800 dark:text-red-200;
}

.basket-combo-card__notice {
  @apply flex items-start gap-3 px-4 py-3;
  @apply bg-amber-50 dark:bg-amber-900/20;
  @apply border-t border-b border-amber-200 dark:border-amber-800;
  @apply text-amber-900 dark:text-amber-100;
}

.basket-combo-card__discount {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  @apply bg-green-50 dark:bg-green-900/20;
  border-radius: 6px;
  font-size: 14px;
  max-width: 100%;
  box-sizing: border-box;
}

.basket-combo-card__discount-label {
  @apply text-green-700 dark:text-green-400;
  font-weight: 500;
}

.basket-combo-card__discount-value {
  @apply text-green-700 dark:text-green-400;
  font-weight: 700;
}

.basket-combo-card__price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  @apply border-t border-gray-200 dark:border-gray-700;
  font-size: 14px;
  @apply text-gray-600 dark:text-gray-400;
}

.basket-combo-card__price-value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #ff6b35;
}

.basket-combo-card__price-old {
  font-size: 14px;
  font-weight: 400;
  @apply text-gray-400 dark:text-gray-500;
  text-decoration: line-through;
}

.basket-combo-card__counter {
  @apply flex-shrink-0;
  min-width: 90px;
  max-width: 110px;
}
</style>
